import React, { useState, useRef, useEffect, useMemo } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { FaChevronDown } from "react-icons/fa";
import styles from "./UserHeaderMenu.module.css";
import { TokenService } from "../../services/Api";
import { getAccessibleMenu } from "./menuConfig";
import { useSelector } from "react-redux";

const UserHeaderMenu = () => {
  const location = useLocation();
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Get menu items based on user role
  const user = TokenService.getUser();
  const userRoleId = user?.role?.id || 2;

  // 🔒 Plan (feature_type) + add-on gating is declared per node in menuConfig
  const menuItems = useMemo(
    () => getAccessibleMenu(userRoleId, user),
    [userRoleId, user],
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  const handleMenuItemClick = (itemId, hasChildren) => {
    if (hasChildren) {
      setActiveDropdown(activeDropdown === itemId ? null : itemId);
    } else {
      setActiveDropdown(null);
      setMobileMenuOpen(false);
    }
  };

  const handleMouseEnter = (itemId, hasChildren) => {
    if (hasChildren && window.innerWidth > 768) {
      setActiveDropdown(itemId);
    }
  };

  const handleMouseLeave = () => {
    if (window.innerWidth > 768) {
      setActiveDropdown(null);
    }
  };

  // const isActiveRoute = (path) => {
  //   console.log("userheadermenu - isActiveRoute",path);

  //   return (
  //     location.pathname === path || location.pathname.startsWith(path + "/")
  //   );
  // };
  const resolvePath = (path) => {
    if (!path) return "";
    return path.startsWith("/") ? path : `/user/${path}`;
  };

  const isActiveRoute = (path) => {
    if (!path) return false;
    const resolvedPath = resolvePath(path);
    return (
      location.pathname === resolvedPath ||
      location.pathname.startsWith(resolvedPath + "/")
    );
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    setActiveDropdown(null);
  };

  const { translations } = useSelector((state) => state.language);

  const getLabelKey = (id) => {
    return id
      .split("-")
      .map((word, index) =>
        index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1),
      )
      .join("");
  };

  const translate = (id, title) => {
    const key = getLabelKey(id);
    return translations?.labels?.[key] || title || id;
  };

  return (
    <nav className={styles.headerMenu} ref={menuRef}>
      <div className={styles.menuContainer}>
        {/* Mobile Menu Toggle */}
        <button
          className={styles.mobileMenuToggle}
          onClick={toggleMobileMenu}
          aria-label="Toggle menu"
        >
          <span
            className={`${styles.hamburger} ${mobileMenuOpen ? styles.active : ""
              }`}
          >
            <span></span>
            <span></span>
            <span></span>
          </span>
        </button>

        {/* Menu Items */}
        <ul
          className={`${styles.menuList} ${mobileMenuOpen ? styles.mobileOpen : ""
            }`}
        >
          {menuItems.map((item) => {
            const IconComponent = item.icon;
            const hasChildren = item.children && item.children.length > 0;
            const isActive = hasChildren
              ? item.children.some((child) => isActiveRoute(child.path))
              : isActiveRoute(item.path);
            const isDropdownOpen = activeDropdown === item.id;

            return (
              <li
                key={item.id}
                className={`${styles.menuItem} ${isActive ? styles.active : ""
                  } ${isDropdownOpen ? styles.dropdownOpen : ""}`}
                onMouseEnter={() => handleMouseEnter(item.id, hasChildren)}
                onMouseLeave={handleMouseLeave}
              >
                {hasChildren ? (
                  <>
                    <button
                      className={`${styles.menuButton} ${isActive ? styles.active : ""
                        }`}
                      onClick={() => handleMenuItemClick(item.id, hasChildren)}
                    >
                      {IconComponent && (
                        <div className={styles.menuIconWrapper}>
                          <IconComponent className={styles.menuIcon} />
                        </div>
                      )}

                      <div className={styles.menuTitleWrapper}>
                        <span className={styles.menuTitle}>
                          {translate(item.id, item.title)}
                        </span>
                        <FaChevronDown
                          className={`${styles.chevron} ${isDropdownOpen ? styles.rotated : ""
                            }`}
                        />
                      </div>
                    </button>

                    {/* Dropdown Menu */}
                    <ul
                      className={`${styles.dropdownMenu} ${isDropdownOpen ? styles.show : ""
                        }`}
                    >
                      {item.children.map((child) => {
                        const ChildIcon = child.icon;
                        return (
                          <li key={child.id} className={styles.dropdownItem}>
                            <NavLink
                              to={child.path}
                              className={({ isActive }) =>
                                `${styles.dropdownLink} ${isActive ? styles.active : ""
                                }`
                              }
                              onClick={() => {
                                setActiveDropdown(null);
                                setMobileMenuOpen(false);
                              }}
                            >
                              {ChildIcon && (
                                <ChildIcon className={styles.dropdownIcon} />
                              )}
                              <span>{translate(child.id, child.title)}</span>
                            </NavLink>
                          </li>
                        );
                      })}
                    </ul>
                  </>
                ) : (
                  <NavLink
                    to={item.path}
                    className={({ isActive }) =>
                      `${styles.menuLink} ${isActive ? styles.active : ""}`
                    }
                    onClick={() => handleMenuItemClick(item.id, hasChildren)}
                  >
                    <div className={styles.menuIconWrapper}>
                      <IconComponent className={styles.menuIcon} />
                    </div>
                    <span className={styles.menuTitle}>
                      {translate(item.id, item.title)}
                    </span>
                  </NavLink>
                )}
              </li>
            );
          })}
        </ul>

        {/* Mobile Overlay */}
        {mobileMenuOpen && (
          <div
            className={styles.mobileOverlay}
            onClick={toggleMobileMenu}
          ></div>
        )}
      </div>
    </nav>
  );
};

export default UserHeaderMenu;
